import { GridItem, SimpleGrid } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LanguageJSON } from '../../../../entities/Language';
import { OfficeJSON } from '../../../../entities/Office';
import AddressSearch, { CitySearchParams } from '../../../AddressSearch';
import { FromDatePicker } from '../../../FromDatePicker';
import { LanguageSelect } from '../../../LanguageSelect';
import { LocationSelector } from '../../../LocationSelector';
import { ParticipantsInput } from '../../../ParticipantsInput';
import { FormControl } from '../../../primitives/FormControl';
import { TimeSlot } from '../../../TimeSlotSelector';
import { TimeSlotSelect } from './TimeSlotSelect';

interface Props {
  participants: number;
  maxParticipants: number;
  areParticipantsValid: boolean;
  date: Date;
  languages: LanguageJSON[];
  languageId: number;
  isVirtual: boolean;
  bookingDeadline?: number;
  isGeneric?: boolean;
  officeId?: number | null;
  offices?: OfficeJSON[];
  onParticipantsChange(participants: number): void;
  onDateChange(date: Date): void;
  onTimeChange(slot: TimeSlot): void;
  onLanguageChange(languageId: number): void;
  onCityChange?(data: CitySearchParams | null): void;
  onOfficeChange?(officeId: number | null): void;
}

export function ExperienceFields(props: Props) {
  const { t } = useTranslation(['experience', 'common']);

  return (
    <SimpleGrid w="100%" columns={2} spacingY={4} spacingX={5}>
      <FromDatePicker
        label={t('form.steps.information.date.label')}
        bookingDeadline={props.bookingDeadline}
        selected={props.date}
        onChange={props.onDateChange}
        inputProps={{ size: 'sm' }}
        isRequired
      />

      <TimeSlotSelect
        date={props.date}
        isRequired={true}
        onChange={props.onTimeChange}
      />

      <ParticipantsInput
        size="sm"
        label={t('form.steps.information.participants.label')}
        defaultParticipants={props.participants}
        maxParticipants={props.maxParticipants}
        onChange={props.onParticipantsChange}
        isParticipantsRangeValid={props.areParticipantsValid}
        isRequired
      />

      <LanguageSelect
        size="sm"
        label={t('form.steps.information.language.label')}
        languages={props.languages}
        languageId={props.languageId}
        placeholder={t('form.steps.information.language.placeholder')}
        isDisabled={props.languages.length === 1}
        onChange={e => props.onLanguageChange(Number(e.target.value))}
        isRequired
      />

      {
        (props.isGeneric || props.isVirtual) && (
          <GridItem colSpan={2}>
            { props.onCityChange && (
              <LocationSelector
                label={t(`form.steps.information.${props.isVirtual ? 'timezone' : 'city'}.label`)}
                officeId={props.officeId}
                offices={props.offices || []}
                onCityChange={props.onCityChange}
                onOfficeChange={props.onOfficeChange}
              />
            ) }
          </GridItem>
        )
      }

      {
        props.officeId === 0 && (
          <GridItem colSpan={2}>
            <FormControl id="addressSearch" label={t('common:fields.officeAddress.label')} isRequired>
              { props.onCityChange && (
                <AddressSearch
                  placeholder={t('common:fields.officeAddress.placeholder')}
                  onChange={props.onCityChange}
                  types={['locality', 'administrative_area_level_3', 'route', 'premise', 'establishment']}
                />
              ) }
            </FormControl>
          </GridItem>
        )
      }
    </SimpleGrid>
  );
}